import React, { useState, useEffect, useMemo } from "react";
import { Dialog } from "@/components/ui/dialog";
import { Button } from "@/components/ui";
import { ArrowRight, MessageSquare, Video, CheckCircle, Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";

interface ReviewSuccessDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  type: "video" | "text";
  onNextReview: () => void;
  onDismiss: () => void;
  onAddVideo?: () => void;
}

const VIDEO_MESSAGES = [
  "A video response puts a face to your brand, turning a transaction into a personal relationship.",
  "A personalized video reply is standard-setting. It shows prospects exactly how responsive you are.",
  "By replying on video, you humanize the lending process and build unmatched digital social proof.",
  "Thoughtful video engagement is one of the most effective ways to drive repeat mortgage business.",
  "Every video reply is a durable asset that helps convert future client referrals on autopilot.",
  "Video replies are opened and watched at a far higher rate than any generic text response.",
  "An active video presence builds authority, showing your local market that you lead with service.",
  "A personalized video thank-you turns a great closing experience into lifelong client advocacy.",
  "Your borrowers connect with people, not transactions. A video reply keeps that connection active.",
  "Responsive video engagement demonstrates the high-touch care that sets top producers apart.",
];

const TEXT_MESSAGES = [
  "Responding to reviews publicly shows prospective borrowers that you value every client experience.",
  "A prompt response turns a simple star rating into a compelling testimonial for your business.",
  "Active engagement on review profiles is a powerful driver for local search engine visibility.",
  "By replying to feedback, you establish a public track record of absolute service excellence.",
  "Consistently responding to reviews shows that your commitment to service extends beyond closing.",
  "Thoughtful replies provide future clients with clear, public evidence of your professionalism.",
  "Engaging with client feedback is a simple, high-signal habit that builds long-term local trust.",
  "A public thank-you reinforces your reputation as a trusted, high-touch mortgage advisor.",
  "Responding to clients publicly helps build a robust, credible brand presence in your market.",
  "Every public response is an opportunity to show future borrowers how you care for your clients.",
];

export function ReviewSuccessDialog({
  open,
  onOpenChange,
  type,
  onNextReview,
  onDismiss,
  onAddVideo,
}: ReviewSuccessDialogProps) {
  const [animateSuccess, setAnimateSuccess] = useState(false);

  // Trigger the animation shortly after component mounts or when opened
  useEffect(() => {
    if (open) {
      setAnimateSuccess(false);
      const animateTimer = setTimeout(() => setAnimateSuccess(true), 700);
      return () => clearTimeout(animateTimer);
    } else return () => {};
  }, [open]);

  // Select a random encouraging phrase based on the response type (video vs text)
  const message = useMemo(() => {
    const list = type === "video" ? VIDEO_MESSAGES : TEXT_MESSAGES;
    const randomIndex = Math.floor(Math.random() * list.length);
    return list[randomIndex];
  }, [type, open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange} size="sm">
      <Dialog.Content className="max-w-md p-8 text-center flex flex-col items-center relative overflow-hidden">
        {/* Elegant Micro-Interaction Success Graphic */}
        <div className="relative flex items-center justify-center w-24 h-24 rounded-full bg-primary/10 mb-6 mt-4 shrink-0 shadow-inner">
          {/* Starting State: Video or MessageSquare Icon */}
          {type === "video" ? (
            <Video
              className={`absolute w-10 h-10 text-primary transition-all duration-500 ease-spring ${
                animateSuccess ? "opacity-0 scale-50 rotate-45" : "opacity-100 scale-100"
              }`}
            />
          ) : (
            <MessageSquare
              className={`absolute w-10 h-10 text-primary transition-all duration-500 ease-spring ${
                animateSuccess ? "opacity-0 scale-50 -rotate-45" : "opacity-100 scale-100"
              }`}
            />
          )}

          {/* Ending State: Emerald Checkmark */}
          <CheckCircle
            className={`absolute w-12 h-12 text-emerald-500 transition-all duration-500 ease-spring delay-300 ${
              animateSuccess ? "opacity-100 scale-100 animate-bounce-decay" : "opacity-0 scale-50"
            }`}
          />
        </div>

        {/* Header */}
        <div className="text-xl font-bold text-slate-800 tracking-tight mb-2">
          {type === "video" ? "Video response complete" : "Reply complete"}
        </div>

        <div className="w-full mb-6 text-center">
          <p className="text-sm font-semibold text-slate-500 leading-relaxed italic">{message}</p>
        </div>

        {/* Actions Stack */}
        <div className="flex flex-col gap-2.5 w-full shrink-0 mb-6">
          {type === "text" && onAddVideo && (
            <Button
              onClick={onAddVideo}
              className="w-full font-bold flex items-center justify-center gap-2 text-sm h-11 shadow-md border-none"
            >
              <Sparkles className="size-4 animate-pulse" />
              Add a Personal Video
              <ArrowRight className="size-4 ml-1" />
            </Button>
          )}

          <Button
            onClick={() => {
              onOpenChange(false);
              onNextReview();
            }}
            variant={type === "text" ? "outline" : "default"}
            className={cn(
              "w-full font-bold flex items-center justify-center gap-2 text-sm h-11 shadow-sm",
              type === "text"
                ? "border-slate-200 hover:bg-slate-50 text-slate-600"
                : "bg-primary hover:bg-primary/90 text-white border-none shadow-md"
            )}
          >
            Next Review
            {!onAddVideo && <ArrowRight className="size-4" />}
          </Button>

          <Button
            onClick={() => {
              onOpenChange(false);
              onDismiss();
            }}
            variant="ghost"
            className="w-full font-bold flex items-center justify-center text-sm h-10 text-slate-400 hover:text-slate-600"
          >
            Close
          </Button>
        </div>
      </Dialog.Content>
    </Dialog>
  );
}
